import { createAuthClient } from 'better-auth/react';
import {
  adminClient,
  phoneNumberClient,
  deviceAuthorizationClient,
  inferAdditionalFields,
  lastLoginMethodClient,
  multiSessionClient,
  oneTimeTokenClient,
  organizationClient,
  twoFactorClient,
  usernameClient,
} from 'better-auth/client/plugins';
import { apiBaseUrl, authBasePath } from './env';
import { locale } from './i18n';

/**
 * The browser's half of the API's Better Auth instance (`packages/framework/src/auth`).
 *
 * `baseURL` is left out when the app talks to its own origin: the client then resolves
 * against `window.location.origin`, which is what the dev proxy and the deployed
 * reverse proxy both present. `basePath` is the server's mount point and is not
 * negotiable — it must match `basePath` in the API's `betterAuth()` call.
 *
 * The plugin list is what gives the client its types; the server has to install the same
 * plugins or a call would 404 rather than be mis-typed:
 *
 * - `usernameClient()`: the API installs the username plugin, so `sign-in/username`
 *   exists there. This is what gives the client the typed `signIn.username` the login
 *   form calls when the operator types a name instead of an address.
 * - `twoFactorClient()`: `twoFactor.*` (enable / verifyTotp / verifyBackupCode /
 *   disable) and the `twoFactorRedirect` flag on a sign-in answer. No `twoFactorPage`
 *   is configured on purpose — that option reloads the whole document, while the login
 *   page keeps the operator in place and asks for the code there.
 * - `multiSessionClient()`: the API installs the multi-session plugin, so one browser may
 *   hold several accounts at once. `multiSession.listDeviceSessions` / `setActive` /
 *   `revoke` are what the 安全设置 pane's account switcher calls — they manage the
 *   accounts saved in *this* browser, which is a different question from "every session
 *   this user has" (the native `listSessions` lists one's own; `/api/admin/users/:id/sessions` is admin-only).
 * - `lastLoginMethodClient()`: reads the API's last-method cookie for login-page hints;
 *   it is a browser preference, never an authentication or authorization credential.
 * - `inferAdditionalFields(...)`: the session's `user` object carries fields the base
 *   model does not have. The schema is declared here as a literal rather than inferred
 *   from the server's `auth` instance because the front end must not import server code;
 *   it is the same declaration shape `additionalFields` uses on the API side, and it is
 *   what makes `session.user.mustChangePassword` a boolean instead of `unknown`.
 */
export const authClient = createAuthClient({
  ...(apiBaseUrl === '' ? {} : { baseURL: apiBaseUrl }),
  basePath: authBasePath,
  fetchOptions: {
    credentials: 'include',
    headers: { 'Accept-Language': locale },
  },
  plugins: [
    organizationClient(),
    // Account management stays behind /users so business ACLs and token filtering apply.
    adminClient(),
    usernameClient(),
    phoneNumberClient(),
    twoFactorClient(),
    multiSessionClient(),
    oneTimeTokenClient(),
    deviceAuthorizationClient(),
    lastLoginMethodClient(),
    inferAdditionalFields({
      user: {
        /**
         * Read-only: the API writes it, the guards and the profile page read it.
         *
         * `input: false` is not decoration. Better Auth turns every declared field into
         * part of the *input* types too, so a plain `{ type: 'boolean' }` would make
         * `mustChangePassword` a required property of `signUp.email`'s body — a public
         * registration form would have to state whether it owes a password change.
         * Declaring it as server-written keeps it out of every request type while still
         * inferring `boolean` on the session's user object.
         */
        mustChangePassword: { type: 'boolean', input: false },
        // The server plugin persists this optional field; the client plugin only reads cookies.
        lastLoginMethod: { type: 'string', required: false, input: false },
      },
    }),
  ],
});

/** `{ session, user }` as the API answers it — the shape the route guards cache. */
export type AuthSession = typeof authClient.$Infer.Session;
export type AuthUser = AuthSession['user'];

/** The session token's expiry, as `Date`. */
export const sessionExpiresAt = (session: AuthSession): Date =>
  new Date(session.session.expiresAt);
